import React from "react";
import { ChevronDown } from "lucide-react";
import ReactMarkdown from "react-markdown";
import MetricItem from "./MetricItem";
import CommonAvatar from "@/shared/components/ui/Avatar/CommonAvatar";
import { formatAccountRevenueLabel } from "@/shared/utils/helperFunctions";
import remarkBreaks from "remark-breaks";

const CompanySection = ({ accordionOpen, toggleAccordion, profileData }) => {
  return (
    <div className="rounded-lg py-7 pr-4 pl-5 shadow mx-[1px] mb-6">
      <div className="w-full">
        <div className="flex ">
          <div className="w-1/4 flex-shrink-0 flex flex-col gap-5 items-center pr-5">
            <CommonAvatar
              imageUrl={profileData?.image}
              name={profileData?.name}
              className="w-20 h-20 rounded text-2xl"
            />
            {/* name and company name */}
            <div className="flex flex-col text-center gap-1.5">
              <h2 className="text-lg font-semibold text-gray-800">
                {profileData?.name}
              </h2>
              <p className="text-sm font-medium text-gray-800">
                {[
                  profileData?.company_size,
                  formatAccountRevenueLabel(profileData?.revenue),
                ]
                  .filter(Boolean)
                  .join(" • ") || "—"}
              </p>
            </div>
          </div>

          {/* Info section */}
          <div className="flex justify-between relative w-full">
            <div className="w-3/4 flex-grow px-5 flex flex-col gap-5">
              {/* Metrics */}
              <div className="flex gap-5 items-center">
                <MetricItem
                  title="Buying Behaviour"
                  value={profileData?.metrics?.[0]?.value}
                  bgcolor="bg-green-50"
                  txtcolor="text-green-600"
                  reason={profileData?.metrics?.[0]?.reason}
                />
                <div className="w-[1.2px] h-full bg-[#EFF0F4]" />
                <MetricItem
                  title="Financial Health"
                  value={profileData?.metrics?.[1]?.value}
                  bgcolor="bg-gray-100"
                  txtcolor="text-gray-600"
                  reason={profileData?.metrics?.[1]?.reason}
                />
                <div className="w-[1.5px] h-full bg-[#EFF0F4]" />
                <MetricItem
                  title="Match Score"
                  value={profileData?.metrics?.[2]?.value}
                  bgcolor="bg-gray-100"
                  txtcolor="text-gray-600"
                  reason={profileData?.metrics?.[2]?.reason}
                />
              </div>
              <div className="flex flex-col gap-1">
                <h5 className="text-sm text-gray-800 font-semibold">
                  Relevance & Recommendations
                </h5>
                <div className="text-gray-500 text-xs">
                  <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                    {profileData?.recommendations}
                  </ReactMarkdown>
                </div>
              </div>

              {profileData?.actions?.length > 0 && (
                <div className="flex gap-5">
                  {profileData.actions.map((action, index) => (
                    <p
                      className="text-xs font-medium text-secondary cursor-pointer hover:text-gray-800"
                      key={index}
                    >
                      {action}
                    </p>
                  ))}
                </div>
              )}

              {/* Expandable Content */}
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  accordionOpen
                    ? "max-h-fit opacity-100 visible"
                    : "max-h-0 opacity-0 invisible"
                }`}
              >
                <div className="flex flex-col gap-6">
                  {profileData?.financial_trends && (
                    <div className="flex flex-col gap-1">
                      <h5 className="text-sm text-gray-800 font-semibold">
                        Financial Trends on Revenue and EBITDA
                      </h5>
                      <div className="text-gray-500 text-xs">
                        <ReactMarkdown>
                          {profileData.financial_trends}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}
                  {profileData?.products_and_services && (
                    <div className="flex flex-col gap-1">
                      <h5 className="text-sm text-gray-800 font-semibold">
                        Products & Services Breakdown
                      </h5>
                      <div className="text-gray-500 text-xs">
                        <ReactMarkdown>
                          {profileData.products_and_services}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}
                  {profileData?.leadership_tree?.length > 0 && (
                    <div className="flex flex-col gap-1">
                      <h5 className="text-sm text-gray-800 font-semibold">
                        Leadership Tree
                      </h5>
                      <ul className="text-gray-500 text-xs space-y-1">
                        {profileData.leadership_tree.map((lead, idx) => (
                          <li key={idx}>
                            <strong>{lead?.name}</strong>
                            {lead?.role && ` — ${lead.role}`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {profileData?.key_pains_priority_strategy && (
                    <div className="flex flex-col gap-1">
                      <h5 className="text-sm text-gray-800 font-semibold">
                        Key Pains & Priority Strategy
                      </h5>
                      <div className="text-gray-500 text-xs">
                        <ReactMarkdown>
                          {profileData?.key_pains_priority_strategy}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* chevron */}
            <button
              className="text-gray-400 hover:text-gray-600 flex items-start"
              onClick={toggleAccordion}
            >
              <ChevronDown
                size={20}
                color="#181D27"
                className={`transition-transform duration-300 ${
                  accordionOpen ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySection;
